import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container"]
  static values = {
    offset: { type: Number, default: 100 }
  }

  connect() {
    this.headings = Array.from(document.querySelectorAll("article h2[id], article h3[id]"))

    if (this.headings.length === 0) {
      // Hide the TOC nav if no headings
      this.element.closest("nav")?.classList.add("hidden")
      return
    }

    // Generate TOC links from headings
    this.generateToc()

    // Use scroll event for more reliable tracking
    this.handleScroll = this.handleScroll.bind(this)
    window.addEventListener("scroll", this.handleScroll, { passive: true })

    // Initial highlight after a brief delay to ensure DOM is ready
    setTimeout(() => this.handleScroll(), 50)
  }

  disconnect() {
    if (this.handleScroll) {
      window.removeEventListener("scroll", this.handleScroll)
    }
  }

  generateToc() {
    if (!this.hasContainerTarget) return

    this.containerTarget.innerHTML = ""

    this.headings.forEach(heading => {
      const link = document.createElement("a")
      link.href = `#${heading.id}`
      link.textContent = heading.textContent
      link.dataset.headingId = heading.id

      // Base classes - inactive state.
      // dark:text-zinc-400 keeps inactive links >=4.5:1 on the zinc-950 dark bg
      // (plain zinc-500 measures ~4.1:1 there and fails WCAG AA).
      const baseClasses = "block py-1 transition-colors duration-150"
      const inactiveClasses = "text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-300"

      // Indent h3 elements
      if (heading.tagName === "H3") {
        link.className = `${baseClasses} ${inactiveClasses} pl-4 text-sm`
      } else {
        link.className = `${baseClasses} ${inactiveClasses}`
      }

      this.containerTarget.appendChild(link)
    })

    // Collect the generated links
    this.links = Array.from(this.containerTarget.querySelectorAll("a[data-heading-id]"))
  }

  handleScroll() {
    if (!this.links || this.links.length === 0) return

    const headerOffset = this.offsetValue
    let activeId = null

    // Find the last heading that has scrolled past the header offset
    for (const heading of this.headings) {
      const rect = heading.getBoundingClientRect()
      if (rect.top <= headerOffset) {
        activeId = heading.id
      }
    }

    // Default to first heading if at the top of the page
    if (!activeId && this.headings.length > 0) {
      activeId = this.headings[0].id
    }

    // Only update if changed
    if (this.currentActiveId !== activeId) {
      this.currentActiveId = activeId
      this.setActive(activeId)
    }
  }

  setActive(id) {
    this.links.forEach(link => {
      const isActive = link.dataset.headingId === id

      // Remove all state classes first
      link.classList.remove(
        "text-zinc-500", "dark:text-zinc-400", "font-medium", "font-normal"
      )

      if (isActive) {
        // Active state: Maquina blue with bold
        link.classList.add("font-medium")
        link.style.color = "#50B1FD"
      } else {
        // Inactive state: muted (theme-aware for WCAG AA in both modes)
        link.classList.add("text-zinc-500", "dark:text-zinc-400", "font-normal")
        link.style.color = ""
      }
    })
  }
}
